type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: unknown;
}

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private readonly maxLogs = 1000;

  private constructor() {
    // Initialize any logging service here
    this.setupErrorHandling();
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private setupErrorHandling() {
    window.addEventListener('error', (event) => {
      this.error('Uncaught error:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.error('Unhandled promise rejection:', {
        reason: event.reason,
      });
    });
  }

  private formatMessage(level: LogLevel, message: string, data?: unknown): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
    };
  }

  private addLog(entry: LogEntry) {
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // You can send logs to your logging service here
    // Example: sendToLoggingService(entry);

    // Also log to console in development
    // Acesso ESTÁTICO a `import.meta.env`: é o que faz o Vite injetar o env
    // neste módulo. Escrito dinamicamente, `DEV` vinha undefined e o log de
    // console nunca saía.
    const isDev = !!import.meta.env.DEV;
    if (isDev) {
      const consoleMethod = entry.level === 'error' ? 'error'
        : entry.level === 'warn' ? 'warn'
          : entry.level === 'info' ? 'info'
            : 'debug';

      const c = console as unknown as Record<string, unknown>;
      const fn = (typeof c?.[consoleMethod] === 'function'
        ? c[consoleMethod]
        : typeof c?.log === 'function'
          ? c.log
          : undefined) as ((...args: unknown[]) => void) | undefined;

      if (fn) {
        fn.call(console, `[${entry.timestamp}] ${entry.level.toUpperCase()}: ${entry.message}`, entry.data || '');
      }
    }
  }

  debug(message: string, data?: unknown) {
    this.addLog(this.formatMessage('debug', message, data));
  }

  info(message: string, data?: unknown) {
    this.addLog(this.formatMessage('info', message, data));
  }

  warn(message: string, data?: unknown) {
    this.addLog(this.formatMessage('warn', message, data));
  }

  error(message: string, data?: unknown) {
    this.addLog(this.formatMessage('error', message, data));
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
  }
}

export const logger = Logger.getInstance();
